/**
 * /dev/cards only: steps the onboarding gallery's demo
 * (templates/dev/_onboarding_gallery.html). Nothing in the app uses it.
 *
 * It drives the production pieces (the onboard-spotlight outlet, swapStep,
 * placeCard, markDots, the onboard-ring done value and the tick attribute),
 * so the gallery shows the real motion rather than a copy of it. One
 * instance per motion mode; the reduced column differs only by
 * --onboard-motion: 0 on an ancestor.
 *
 * Every spotlight layer in its column is an outlet. The stepper's plays the
 * walkthrough's four steps, each template naming its lit stand-in
 * (data-spot) and its cutout's shape (data-shape); a layer marked
 * data-gallery-veil (the offer's) is dimmed with nothing lit.
 *
 * The send cards are drawn from <template>s without onboard_send_controller,
 * which reports send_prompt events on sight and would record them for whoever
 * opens the gallery (beta shares production's database). Their motion is
 * onboarding.css's own, keyed on the same data-state and data-folding the
 * controller sets; their close buttons are pointed here instead, and their
 * way to the client view goes nowhere. The client preview's strip likewise
 * has no client_preview_controller, which records a client view on sight;
 * its peak and line swaps are played from here with the same helpers.
 * Step 2a's address list is the live AddressAutocomplete
 * (static/js/address_autocomplete.js) offered through the fill's offerPick.
 */
import { Controller } from '@hotwired/stimulus';

import { animationsFinished, markDots, placeCard, playPeak, settlePeak, swapStep, swapText } from '../_onboard.js';
import { offerPick } from '../_walkthrough_fill.js';

export default class extends Controller {
    static targets = ['card', 'viewport', 'step', 'ring', 'tick', 'sendStage', 'sendCard', 'sendFold', 'strip',
        'address'];
    static outlets = ['onboard-spotlight'];

    connect() {
        this._index = 0;
        this.arriveSend();
        this.unfoldSend();
        if (this.hasStripTarget) {
            this._stripLine = this._line().textContent;
            this.replayPeak();
        }
        this._addressFrame = requestAnimationFrame(() => this._buildAddresses());
    }

    onboardSpotlightOutletConnected(outlet, element) {
        if (element.hasAttribute('data-gallery-veil')) {
            outlet.veil();
            return;
        }
        this._spotlight = outlet;
        this._show(this._index, null);
    }

    next() {
        this._go(1);
    }

    back() {
        this._go(-1);
    }

    /** Light the current step again, so the arrow draws and nudges once more. */
    replay() {
        this._show(this._index, null);
    }

    advanceRings() {
        for (const ring of this.ringTargets) {
            const done = Number(ring.dataset.onboardRingDoneValue);
            const total = Number(ring.dataset.onboardRingTotalValue);
            ring.dataset.onboardRingDoneValue = String(done >= total ? 0 : done + 1);
        }
    }

    toggleTicks() {
        for (const tick of this.tickTargets) tick.toggleAttribute('data-done');
    }

    /** The send card rising over the stand-in nav, as after a first save. */
    arriveSend() {
        if (!this.hasSendStageTarget) return;
        this.sendStageTarget.querySelector('.onboard-send')?.remove();
        const card = inertSendCard(this.sendCardTarget, 'closeSend');
        this.sendStageTarget.append(card);
        requestAnimationFrame(async () => {
            card.dataset.state = 'in';
            await animationsFinished(card);
            card.querySelector('[data-onboard-send-target="tick"]')?.setAttribute('data-done', '');
        });
    }

    async closeSend() {
        const card = this.sendStageTarget.querySelector('.onboard-send[data-state="in"]');
        if (!card) return;
        card.dataset.state = 'out';
        await animationsFinished(card);
        card.remove();
    }

    /** Folds the first quote page card away, as its close or a send does. */
    async foldSend() {
        const wrapper = this.sendFoldTargets.find((fold) => fold.querySelector('.onboard-send'));
        if (!wrapper || wrapper.hasAttribute('data-folding')) return;
        const card = wrapper.querySelector('.onboard-send');
        wrapper.setAttribute('data-folding', '');
        await Promise.all([animationsFinished(card), animationsFinished(wrapper)]);
        card.remove();
        wrapper.removeAttribute('data-folding');
    }

    unfoldSend() {
        for (const wrapper of this.sendFoldTargets) {
            if (wrapper.querySelector('.onboard-send')) continue;
            wrapper.append(inertSendCard(wrapper.querySelector('template'), 'foldSend'));
        }
    }

    /** The confirm card after a refused resend: Ask Chip in place of the client view. */
    refuseResend() {
        for (const card of this.element.querySelectorAll('.onboard-send-fold .onboard-send')) {
            const ask = card.querySelector('[data-onboard-send-target="ask"]');
            if (!ask) continue;
            ask.hidden = false;
            card.querySelector('[data-onboard-send-target="preview"]')?.setAttribute('hidden', '');
        }
    }

    /**
     * Step 2a: each field gets the live address autocomplete, and the fill's
     * own offerPick drops the sample's suggestions with the first marked.
     * Built a frame after connect, because the autocomplete wraps its input:
     * moving it during Stimulus's connect pass lets a column's spotlight
     * outlet arrive before that spotlight has connected. The field is
     * readonly, so nothing typed there searches.
     */
    _buildAddresses() {
        for (const input of this.addressTargets) {
            input.autocompleteInstance ??= new window.AddressAutocomplete(input);
            this._offerPick(input);
        }
    }

    disconnect() {
        cancelAnimationFrame(this._addressFrame);
        for (const input of this.addressTargets) {
            input.autocompleteInstance?.destroy();
            input.autocompleteInstance = null;
        }
    }

    /** The list again, after a tap elsewhere on the page closed it. */
    replayPick() {
        for (const input of this.addressTargets) this._offerPick(input);
    }

    _offerPick(input) {
        offerPick(input.autocompleteInstance, JSON.parse(input.dataset.suggestions));
    }

    /** The peak, as on a person's first client view. */
    replayPeak() {
        playPeak(this.stripTarget.querySelector('.onboard-peak'));
    }

    /** The strip's line swapping for what a tapped Accept or PDF link says. */
    sayStrip({ params: { say } }) {
        const text = this.stripTarget.querySelector(`[data-say="${say}"]`).textContent;
        if (this._line().textContent !== text) swapText(this._line(), text);
    }

    resetStrip() {
        settlePeak(this.stripTarget.querySelector('.onboard-peak'));
        this._line().textContent = this._stripLine;
    }

    _line() {
        return this.stripTarget.querySelector('.onboard-client-strip__line');
    }

    _go(delta) {
        const count = this.stepTargets.length;
        this._index = (this._index + delta + count) % count;
        this._show(this._index, delta > 0 ? 'forward' : 'back');
    }

    _show(index, direction) {
        const template = this.stepTargets[index];
        const step = template.content.firstElementChild.cloneNode(true);
        if (direction) swapStep(this.viewportTarget, step, direction);
        else this.viewportTarget.replaceChildren(step);
        markDots(this.cardTarget.querySelector('.onboard-dots'), index);
        this.cardTarget.closest('.gallery-phone')
            ?.toggleAttribute('data-launcher-hidden', template.hasAttribute('data-launcher-hidden'));
        const target = this.element.querySelector(template.dataset.spot);
        const frame = this._spotlight.pointAt(target, { shape: template.dataset.shape || null });
        placeCard(this.cardTarget, frame.card);
    }
}

/**
 * A send card from `template` with no controller of its own; its close calls
 * `closeMethod` here and its way to the client view leads nowhere.
 */
function inertSendCard(template, closeMethod) {
    const card = template.content.firstElementChild.cloneNode(true);
    card.removeAttribute('data-controller');
    card.removeAttribute('data-action');
    card.querySelector('.onboard-checklist__close')
        ?.setAttribute('data-action', `dev--onboard-gallery#${closeMethod}`);
    card.querySelector('[data-client-preview-link]')?.removeAttribute('href');
    return card;
}
